// Certificates.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  FlatList,
  Image,
  Dimensions,
  Alert,
  Platform,
  Animated,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Share,
  Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  MaterialCommunityIcons, 
  Ionicons, 
  Feather,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import * as FileSystem from 'expo-file-system';
import * as MediaLibrary from 'expo-media-library';
import * as Print from 'expo-print';

const { width, height } = Dimensions.get('window');

const Certificates = () => {
  const navigation = useNavigation();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'recent', 'pinned', 'verified'
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    totalCertificates: 0,
    pinnedCertificates: 0,
    verifiedCertificates: 0,
    totalHours: 0,
    totalCourses: 0,
    recentCertificate: null,
  });
  
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  const scrollY = useRef(new Animated.Value(0)).current;

  // Animation for header
  const headerHeight = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [160, 100],
    extrapolate: 'clamp',
  });

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0.9],
    extrapolate: 'clamp',
  });

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
      startAnimations();
      return () => {};
    }, [])
  );

  useEffect(() => {
    loadCertificates();
    loadDarkModePreference();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCertificates(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadCertificates = async () => {
    try {
      // Load certificates from AsyncStorage
      const savedCertificates = await AsyncStorage.getItem('certificates');
      if (savedCertificates) {
        const parsedCertificates = JSON.parse(savedCertificates);
        setCertificates(parsedCertificates);
        calculateStats(parsedCertificates);
      } else {
        // Generate mock certificates based on completed courses
        await generateMockCertificates();
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
      await generateMockCertificates();
    }
  };

  const generateMockCertificates = async () => {
    try {
      // Get completed courses
      const completedCourses = await AsyncStorage.getItem('completedCourses') || '[]';
      const courses = JSON.parse(completedCourses);
      
      // Generate certificates from completed courses
      const mockCertificates = courses.map((course, index) => ({
        id: `CERT-${Date.now()}-${index}`,
        certificateId: `CERT-${1000 + index}`,
        courseId: course.id,
        title: course.title,
        subtitle: course.subtitle,
        instructor: course.instructor,
        studentName: 'John Doe', // In real app, get from user profile
        issueDate: course.completedDate || new Date().toISOString().split('T')[0],
        expiryDate: course.expiryDate || null,
        grade: course.grade || 'A',
        score: course.score || 95,
        duration: course.duration || '20 hours',
        category: course.category || 'general',
        verificationCode: `ENG-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        verificationUrl: 'https://verify.engineeringlearning.com',
        isPinned: index === 0, // First certificate is pinned
        isVerified: true,
        color: course.color || '#667eea',
        icon: course.icon || 'certificate',
        thumbnail: course.thumbnail || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400',
        certificateUrl: null, // Will be generated on demand
        sharedCount: 0,
        lastViewed: new Date().toISOString(),
        tags: ['Engineering', 'Professional', course.category],
      }));

      // Add some additional mock certificates
      const additionalCertificates = [
        {
          id: 'CERT-M001',
          certificateId: 'CERT-1001',
          courseId: 'MC001',
          title: 'Professional Engineering Certification',
          subtitle: 'Licensed Professional Engineer (PE)',
          instructor: 'Engineering Certification Board',
          studentName: 'John Doe',
          issueDate: '2023-12-15',
          expiryDate: '2025-12-15',
          grade: 'PASS',
          score: 88,
          duration: '40 hours',
          category: 'professional',
          verificationCode: 'PEC-2023-00123',
          verificationUrl: 'https://verify.professionalengineer.org',
          isPinned: true,
          isVerified: true,
          color: '#0e4e96',
          icon: 'account-tie',
          thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400',
          sharedCount: 3,
          lastViewed: '2024-02-15',
          tags: ['Professional', 'Licensed', 'Advanced'],
        },
        {
          id: 'CERT-M002',
          certificateId: 'CERT-1002',
          courseId: 'WS001',
          title: 'Advanced CAD Workshop',
          subtitle: 'Autodesk Fusion 360 Mastery',
          instructor: 'Autodesk Certified Instructor',
          studentName: 'John Doe',
          issueDate: '2024-01-20',
          expiryDate: null,
          grade: 'A+',
          score: 98,
          duration: '15 hours',
          category: 'mechanical',
          verificationCode: 'CAD-2024-04567',
          verificationUrl: 'https://verify.autodesk.com',
          isPinned: false,
          isVerified: true,
          color: '#FF6B35',
          icon: 'cog',
          thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
          sharedCount: 1,
          lastViewed: '2024-02-10',
          tags: ['CAD', 'Mechanical', 'Workshop'],
        },
      ];

      const allCertificates = [...mockCertificates, ...additionalCertificates];
      setCertificates(allCertificates);
      await AsyncStorage.setItem('certificates', JSON.stringify(allCertificates));
      calculateStats(allCertificates);
    } catch (error) {
      console.error('Error generating mock certificates:', error);
    }
  };

  const calculateStats = (certs) => {
    const totalCertificates = certs.length;
    const pinnedCertificates = certs.filter(c => c.isPinned).length;
    const verifiedCertificates = certs.filter(c => c.isVerified).length;
    const totalHours = certs.reduce((sum, cert) => {
      const hours = parseInt(cert.duration) || 0;
      return sum + hours;
    }, 0);
    const totalCourses = new Set(certs.map(c => c.courseId)).size;
    const recentCertificate = certs.length > 0 ? 
      certs.reduce((latest, cert) => 
        new Date(cert.issueDate) > new Date(latest.issueDate) ? cert : latest
      ) : null;

    setStats({
      totalCertificates,
      pinnedCertificates,
      verifiedCertificates,
      totalHours,
      totalCourses,
      recentCertificate,
    });
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadCertificates();
    } catch (error) {
      console.error('Error refreshing certificates:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const togglePinCertificate = async (certificateId) => {
    try {
      const updatedCertificates = certificates.map(cert => 
        cert.id === certificateId 
          ? { ...cert, isPinned: !cert.isPinned }
          : cert
      );
      
      setCertificates(updatedCertificates);
      await AsyncStorage.setItem('certificates', JSON.stringify(updatedCertificates));
      
      // Update stats
      calculateStats(updatedCertificates);
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const shareCertificate = async (certificate) => {
    try {
      const result = await Share.share({
        title: `🎓 Certificate: ${certificate.title}`,
        message: `I earned a certificate in ${certificate.title}!\n\nScore: ${certificate.score}%\nVerification Code: ${certificate.verificationCode}\n\nVerify at: ${certificate.verificationUrl}`,
        url: certificate.verificationUrl,
      });
      
      if (result.action === Share.sharedAction) {
        // Update share count
        const updatedCertificates = certificates.map(cert => 
          cert.id === certificate.id 
            ? { ...cert, sharedCount: (cert.sharedCount || 0) + 1 }
            : cert
        );
        
        setCertificates(updatedCertificates);
        await AsyncStorage.setItem('certificates', JSON.stringify(updatedCertificates));
      }
    } catch (error) {
      console.error('Error sharing certificate:', error);
      Alert.alert('Error', 'Failed to share certificate');
    }
  };

  const downloadCertificate = async (certificate) => {
    try {
      // Request permissions for Android
      if (Platform.OS === 'android') {
        const { status } = await MediaLibrary.requestPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert('Permission required', 'Please grant storage permissions to download certificates');
          return;
        }
      }

      // Generate PDF certificate
      const html = generateCertificateHTML(certificate);
      
      const { uri } = await Print.printToFileAsync({
        html,
        width: 842,
        height: 595, // A4 size in points
        base64: false,
      });

      // Save to downloads
      const downloadsDir = FileSystem.documentDirectory + 'Downloads/';
      await FileSystem.makeDirectoryAsync(downloadsDir, { intermediates: true });
      
      const fileName = `Certificate_${certificate.certificateId}_${Date.now()}.pdf`;
      const newPath = downloadsDir + fileName;
      
      await FileSystem.moveAsync({
        from: uri,
        to: newPath,
      });

      // For Android, move to public downloads
      if (Platform.OS === 'android') {
        const asset = await MediaLibrary.createAssetAsync(newPath);
        await MediaLibrary.createAlbumAsync('Engineering Certificates', asset, false);
      }

      Alert.alert(
        'Success',
        'Certificate downloaded successfully!',
        [{ text: 'OK' }]
      );

    } catch (error) {
      console.error('Error downloading certificate:', error);
      Alert.alert('Error', 'Failed to download certificate');
    }
  };

  const generateCertificateHTML = (certificate) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Certificate of Completion</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Roboto:wght@300;400;500&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Roboto', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 20px;
          }
          
          .certificate-container {
            width: 842px;
            height: 595px;
            background: white;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            position: relative;
            overflow: hidden;
            border: 15px solid #f8fafc;
          }
          
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 120px;
            color: rgba(102, 126, 234, 0.1);
            font-weight: bold;
            white-space: nowrap;
            z-index: 1;
          }
          
          .header {
            background: linear-gradient(135deg, ${certificate.color || '#667eea'} 0%, ${certificate.color ? `${certificate.color}80` : '#764ba2'} 100%);
            padding: 40px;
            text-align: center;
            color: white;
            position: relative;
            z-index: 2;
          }
          
          .header h1 {
            font-family: 'Playfair Display', serif;
            font-size: 48px;
            margin-bottom: 10px;
            letter-spacing: 2px;
          }
          
          .header h2 {
            font-size: 20px;
            font-weight: 300;
            opacity: 0.9;
          }
          
          .content {
            padding: 40px;
            text-align: center;
            position: relative;
            z-index: 2;
          }
          
          .awarded-to {
            font-size: 18px;
            color: #6b7280;
            margin-bottom: 30px;
          }
          
          .student-name {
            font-family: 'Playfair Display', serif;
            font-size: 64px;
            color: #1f2937;
            margin-bottom: 40px;
            font-weight: bold;
            background: linear-gradient(135deg, ${certificate.color || '#667eea'} 0%, ${certificate.color ? `${certificate.color}80` : '#764ba2'} 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          
          .course-title {
            font-size: 32px;
            color: #374151;
            margin-bottom: 20px;
            font-weight: 500;
          }
          
          .course-subtitle {
            font-size: 20px;
            color: #6b7280;
            margin-bottom: 40px;
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
          }
          
          .details {
            display: flex;
            justify-content: space-around;
            margin: 40px 0;
            padding: 30px;
            background: #f9fafb;
            border-radius: 15px;
          }
          
          .detail-item {
            text-align: center;
          }
          
          .detail-label {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .detail-value {
            font-size: 20px;
            color: #1f2937;
            font-weight: 500;
          }
          
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
            padding-top: 40px;
            border-top: 2px solid #e5e7eb;
          }
          
          .signature {
            text-align: center;
          }
          
          .signature-name {
            font-size: 18px;
            color: #1f2937;
            font-weight: 500;
            margin-top: 20px;
          }
          
          .signature-title {
            font-size: 14px;
            color: #6b7280;
            margin-top: 5px;
          }
          
          .verification {
            text-align: center;
            margin-top: 30px;
            font-size: 12px;
            color: #9ca3af;
          }
          
          .verification-code {
            font-family: monospace;
            font-size: 14px;
            color: ${certificate.color || '#667eea'};
            font-weight: bold;
            margin-top: 5px;
          }
          
          .footer {
            position: absolute;
            bottom: 20px;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 12px;
            color: #9ca3af;
          }
          
          .seal {
            position: absolute;
            top: 40px;
            right: 40px;
            width: 100px;
            height: 100px;
            background: ${certificate.color || '#667eea'};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 14px;
            text-align: center;
            transform: rotate(15deg);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          }
        </style>
      </head>
      <body>
        <div class="certificate-container">
          <div class="watermark">CERTIFICATE OF COMPLETION</div>
          
          <div class="seal">
            ENGINEERING<br>LEARNING<br>HUB
          </div>
          
          <div class="header">
            <h1>CERTIFICATE</h1>
            <h2>of Achievement</h2>
          </div>
          
          <div class="content">
            <div class="awarded-to">This certificate is proudly awarded to</div>
            <div class="student-name">${certificate.studentName}</div>
            
            <div class="course-title">${certificate.title}</div>
            <div class="course-subtitle">${certificate.subtitle}</div>
            
            <div class="details">
              <div class="detail-item">
                <div class="detail-label">Instructor</div>
                <div class="detail-value">${certificate.instructor}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Grade</div>
                <div class="detail-value">${certificate.grade}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Date</div>
                <div class="detail-value">${certificate.issueDate}</div>
              </div>
            </div>
            
            <div class="signatures">
              <div class="signature">
                <svg width="120" height="60" viewBox="0 0 120 60">
                  <path d="M10,30 Q30,10 50,30 Q70,50 90,30 Q110,10 110,30" 
                        stroke="${certificate.color || '#667eea'}" 
                        stroke-width="2" 
                        fill="none"/>
                </svg>
                <div class="signature-name">${certificate.instructor}</div>
                <div class="signature-title">Course Instructor</div>
              </div>
              
              <div class="signature">
                <svg width="120" height="60" viewBox="0 0 120 60">
                  <path d="M10,30 Q40,5 70,30 Q100,55 110,30" 
                        stroke="${certificate.color || '#667eea'}" 
                        stroke-width="2" 
                        fill="none"/>
                </svg>
                <div class="signature-name">Dr. Robert Chen</div>
                <div class="signature-title">Dean of Engineering</div>
              </div>
            </div>
            
            <div class="verification">
              Verification Code: 
              <div class="verification-code">${certificate.verificationCode}</div>
              Verify at: ${certificate.verificationUrl}
            </div>
          </div>
          
          <div class="footer">
            © ${new Date().getFullYear()} Engineering Learning Hub. All rights reserved.
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const verifyCertificate = (certificate) => {
    Linking.openURL(certificate.verificationUrl).catch(err => 
      console.error('Error opening verification URL:', err)
    );
  };

  const filterCertificates = () => {
    let filtered = certificates;
    
    // Apply filter
    switch (filter) {
      case 'pinned':
        filtered = filtered.filter(cert => cert.isPinned);
        break;
      case 'verified':
        filtered = filtered.filter(cert => cert.isVerified);
        break;
      case 'recent':
        filtered = filtered.sort((a, b) => 
          new Date(b.issueDate) - new Date(a.issueDate)
        ).slice(0, 5);
        break;
      default:
        // 'all' - show all certificates
        break;
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cert =>
        String(cert.title ?? '').toLowerCase().includes(query) ||
        String(cert.subtitle ?? '').toLowerCase().includes(query) ||
        String(cert.instructor ?? '').toLowerCase().includes(query) ||
        String(cert.certificateId ?? '').toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#1f2937' }]}>
        Loading Your Certificates...
      </Text>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          height: headerHeight,
          opacity: headerOpacity,
        },
      ]}
    >
      <LinearGradient
        colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.menuButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>🏆 Certificates</Text>
          
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => navigation.navigate('AddCertificate')}
          >
            <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Your achievements and professional certifications
        </Text>
        
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.totalCertificates}</Text>
            <Text style={styles.statLabel}>Total</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.pinnedCertificates}</Text>
            <Text style={styles.statLabel}>Pinned</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.verifiedCertificates}</Text>
            <Text style={styles.statLabel}>Verified</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.totalHours}h</Text>
            <Text style={styles.statLabel}>Hours</Text>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderFilters = () => (
    <View style={styles.filtersContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {[
          { id: 'all', label: 'All Certificates', icon: 'certificate' },
          { id: 'pinned', label: 'Pinned', icon: 'pin' },
          { id: 'verified', label: 'Verified', icon: 'check-decagram' },
          { id: 'recent', label: 'Recent', icon: 'clock' },
          { id: 'professional', label: 'Professional', icon: 'account-tie' },
          { id: 'academic', label: 'Academic', icon: 'school' },
        ].map((filterItem) => (
          <TouchableOpacity
            key={filterItem.id}
            style={[
              styles.filterButton,
              { backgroundColor: darkMode ? '#2d3748' : '#ffffff' },
              filter === filterItem.id && { backgroundColor: '#667eea' }
            ]}
            onPress={() => setFilter(filterItem.id)}
          >
            <MaterialCommunityIcons 
              name={filterItem.icon} 
              size={16} 
              color={filter === filterItem.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.filterText,
              { color: filter === filterItem.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
            ]}>
              {filterItem.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderCertificateCard = (certificate) => (
    <Animated.View 
      style={[
        styles.certificateCard,
        {
          opacity: fadeAnim,
          transform: [{
            scale: scaleAnim,
          }],
        },
      ]}
      key={certificate.id}
    >
      <TouchableOpacity
        style={[styles.certificateCardContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => {
          setSelectedCertificate(certificate);
          setShowCertificateModal(true);
        }}
        activeOpacity={0.9}
      >
        <View style={styles.certificateHeader}>
          <LinearGradient
            colors={[certificate.color, `${certificate.color}80`]}
            style={styles.certificateIcon}
          >
            <MaterialCommunityIcons name={certificate.icon} size={24} color="#ffffff" />
          </LinearGradient>
          
          <View style={styles.certificateTitleContainer}>
            <Text style={[styles.certificateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={1}>
              {certificate.title}
            </Text>
            <Text style={[styles.certificateId, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              ID: {certificate.certificateId}
            </Text>
          </View>
          
          <TouchableOpacity 
            style={styles.pinButton}
            onPress={(e) => {
              e.stopPropagation();
              togglePinCertificate(certificate.id);
            }}
          >
            <MaterialCommunityIcons
              name={certificate.isPinned ? "pin" : "pin-outline"}
              size={20}
              color={certificate.isPinned ? "#f59e0b" : (darkMode ? "#a0aec0" : "#6b7280")}
            />
          </TouchableOpacity>
        </View>
        
        <Text style={[styles.certificateSubtitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]} numberOfLines={2}>
          {certificate.subtitle}
        </Text>
        
        <View style={styles.certificateDetails}>
          <View style={styles.detailRow}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="account" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {certificate.instructor}
              </Text>
            </View>
            
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="calendar" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {certificate.issueDate}
              </Text>
            </View>
          </View>
          
          <View style={styles.detailRow}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="clock-outline" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {certificate.duration}
              </Text>
            </View>
            
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="star" size={14} color="#fbbf24" />
              <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Grade: {certificate.grade}
              </Text>
            </View>
          </View>
        </View>
        
        <View style={styles.certificateFooter}>
          {certificate.isVerified && (
            <View style={styles.verifiedBadge}>
              <MaterialCommunityIcons name="check-decagram" size={14} color="#10b981" />
              <Text style={styles.verifiedText}>Verified</Text>
            </View>
          )}
          
          <View style={styles.certificateTags}>
            {certificate.tags?.slice(0, 2).map((tag, index) => (
              <View key={index} style={[styles.tag, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
                <Text style={[styles.tagText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>{tag}</Text>
              </View>
            ))}
          </View>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="certificate-outline" size={64} color={darkMode ? '#4a5568' : '#d1d5db'} />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Certificates Yet
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {searchQuery 
          ? 'No certificates match your search'
          : 'Complete courses to earn certificates!'}
      </Text>
      
      <TouchableOpacity
        style={styles.emptyStateButton}
        onPress={() => navigation.navigate('MyCourses')}
      >
        <MaterialCommunityIcons name="book-open-variant" size={20} color="#ffffff" />
        <Text style={styles.emptyStateButtonText}>Browse Courses</Text>
      </TouchableOpacity>
    </View>
  );

  const renderCertificateModal = () => (
    <Modal
      visible={showCertificateModal}
      animationType="slide"
      onRequestClose={() => setShowCertificateModal(false)}
    >
      {selectedCertificate && (
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
          <View style={[styles.modalHeader, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity onPress={() => setShowCertificateModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
            
            <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]} numberOfLines={1}>
              Certificate Details
            </Text>
            
            <TouchableOpacity onPress={() => setShowShareOptions(true)}>
              <MaterialCommunityIcons name="share-variant" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            <LinearGradient
              colors={[selectedCertificate.color, `${selectedCertificate.color}80`]}
              style={styles.modalCertificateHeader}
            >
              <View style={styles.modalCertificateIcon}>
                <MaterialCommunityIcons name={selectedCertificate.icon} size={40} color="#ffffff" />
              </View>
              
              <Text style={styles.modalCertificateTitle}>{selectedCertificate.title}</Text>
              <Text style={styles.modalCertificateSubtitle}>{selectedCertificate.subtitle}</Text>
              
              <View style={styles.modalCertificateId}>
                <Text style={styles.modalCertificateIdText}>
                  Certificate ID: {selectedCertificate.certificateId}
                </Text>
              </View>
            </LinearGradient>
            
            <View style={styles.modalCertificateInfo}>
              <View style={styles.infoRow}>
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="account" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Awarded To
                    </Text>
                    <Text style={[styles.infoValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCertificate.studentName}
                    </Text>
                  </View>
                </View>
                
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="school" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Instructor
                    </Text>
                    <Text style={[styles.infoValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCertificate.instructor}
                    </Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.infoRow}>
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="calendar" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Issue Date
                    </Text>
                    <Text style={[styles.infoValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCertificate.issueDate}
                    </Text>
                  </View>
                </View>
                
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="clock-outline" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Duration
                    </Text>
                    <Text style={[styles.infoValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCertificate.duration}
                    </Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.infoRow}>
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="star" size={20} color="#fbbf24" />
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Grade & Score
                    </Text>
                    <Text style={[styles.infoValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedCertificate.grade} • {selectedCertificate.score}%
                    </Text>
                  </View>
                </View>
                
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="shield-check" size={20} color="#10b981" />
                  <View style={styles.infoContent}>
                    <Text style={[styles.infoLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Status
                    </Text>
                    <Text style={[styles.infoValue, { color: '#10b981' }]}>
                      {selectedCertificate.isVerified ? 'Verified ✓' : 'Pending'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
            
            {selectedCertificate.tags && selectedCertificate.tags.length > 0 && (
              <View style={styles.modalTags}>
                <Text style={[styles.modalTagsTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  Tags
                </Text>
                <View style={styles.modalTagsList}>
                  {selectedCertificate.tags.map((tag, index) => (
                    <View 
                      key={index} 
                      style={[styles.modalTag, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
                    >
                      <Text style={[styles.modalTagText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {tag}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
            
            <View style={styles.verificationSection}>
              <Text style={[styles.verificationTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                Verification
              </Text>
              <View style={[styles.verificationCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                <Text style={[styles.verificationCode, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  {selectedCertificate.verificationCode}
                </Text>
                <Text style={[styles.verificationText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Use this code to verify your certificate authenticity
                </Text>
                <TouchableOpacity
                  style={styles.verifyButton}
                  onPress={() => verifyCertificate(selectedCertificate)}
                >
                  <MaterialCommunityIcons name="shield-check" size={20} color="#ffffff" />
                  <Text style={styles.verifyButtonText}>Verify Online</Text>
                </TouchableOpacity>
              </View>
            </View>
            
            <View style={styles.shareStats}>
              <Text style={[styles.shareStatsTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                Sharing Stats
              </Text>
              <View style={styles.shareStatsRow}>
                <View style={styles.shareStat}>
                  <MaterialCommunityIcons name="eye" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.shareStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {Math.floor(Math.random() * 100)}
                  </Text>
                  <Text style={[styles.shareStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Views
                  </Text>
                </View>
                
                <View style={styles.shareStat}>
                  <MaterialCommunityIcons name="share-variant" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.shareStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedCertificate.sharedCount || 0}
                  </Text>
                  <Text style={[styles.shareStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Shares
                  </Text>
                </View>
                
                <View style={styles.shareStat}>
                  <MaterialCommunityIcons name="download" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.shareStatNumber, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {Math.floor(Math.random() * 20)}
                  </Text>
                  <Text style={[styles.shareStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Downloads
                  </Text>
                </View>
              </View>
            </View>
          </ScrollView>
          
          <View style={[styles.modalActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity
              style={[styles.modalActionButton, styles.downloadButton]}
              onPress={() => downloadCertificate(selectedCertificate)}
            >
              <MaterialCommunityIcons name="download" size={20} color="#ffffff" />
              <Text style={styles.downloadButtonText}>Download PDF</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.modalActionButton, styles.shareButton]}
              onPress={() => shareCertificate(selectedCertificate)}
            >
              <MaterialCommunityIcons name="share-variant" size={20} color="#ffffff" />
              <Text style={styles.shareButtonText}>Share</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderShareOptionsModal = () => (
    <Modal
      visible={showShareOptions}
      animationType="fade"
      transparent={true}
      onRequestClose={() => setShowShareOptions(false)}
    >
      <TouchableOpacity 
        style={styles.shareOverlay}
        activeOpacity={1}
        onPress={() => setShowShareOptions(false)}
      >
        <View style={[styles.shareModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <Text style={[styles.shareModalTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            Share Certificate
          </Text>
          
          <View style={styles.shareOptions}>
            <TouchableOpacity
              style={styles.shareOption}
              onPress={() => {
                setShowShareOptions(false);
                shareCertificate(selectedCertificate);
              }}
            >
              <View style={[styles.shareOptionIcon, { backgroundColor: '#667eea' }]}>
                <MaterialCommunityIcons name="share-variant" size={24} color="#ffffff" />
              </View>
              <Text style={[styles.shareOptionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Share via
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.shareOption}
              onPress={() => {
                setShowShareOptions(false);
                downloadCertificate(selectedCertificate);
              }}
            >
              <View style={[styles.shareOptionIcon, { backgroundColor: '#10b981' }]}>
                <MaterialCommunityIcons name="download" size={24} color="#ffffff" />
              </View>
              <Text style={[styles.shareOptionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Download PDF
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.shareOption}
              onPress={() => {
                setShowShareOptions(false);
                if (selectedCertificate) {
                  // Generate HTML for sharing
                  const html = generateCertificateHTML(selectedCertificate);
                  // You could implement email sharing with HTML attachment here
                  Alert.alert('Info', 'Email sharing would be implemented with a proper email service');
                }
              }}
            >
              <View style={[styles.shareOptionIcon, { backgroundColor: '#f59e0b' }]}>
                <MaterialCommunityIcons name="email" size={24} color="#ffffff" />
              </View>
              <Text style={[styles.shareOptionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Email as PDF
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.shareOption}
              onPress={() => {
                setShowShareOptions(false);
                verifyCertificate(selectedCertificate);
              }}
            >
              <View style={[styles.shareOptionIcon, { backgroundColor: '#3b82f6' }]}>
                <MaterialCommunityIcons name="link" size={24} color="#ffffff" />
              </View>
              <Text style={[styles.shareOptionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Copy Link
              </Text>
            </TouchableOpacity>
          </View>
          
          <TouchableOpacity
            style={styles.cancelShareButton}
            onPress={() => setShowShareOptions(false)}
          >
            <Text style={styles.cancelShareText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const filteredCertificates = filterCertificates();

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar 
        barStyle="light-content" 
        backgroundColor="#667eea" 
      />
      
      {renderHeader()}
      
      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
      >
        {renderFilters()}
        
        {stats.recentCertificate && (
          <View style={styles.recentCertificateSection}>
            <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
              🏅 Most Recent Certificate
            </Text>
            <TouchableOpacity
              style={[styles.recentCertificate, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
              onPress={() => {
                setSelectedCertificate(stats.recentCertificate);
                setShowCertificateModal(true);
              }}
            >
              <LinearGradient
                colors={[stats.recentCertificate.color, `${stats.recentCertificate.color}80`]}
                style={styles.recentCertificateIcon}
              >
                <MaterialCommunityIcons name={stats.recentCertificate.icon} size={32} color="#ffffff" />
              </LinearGradient>
              
              <View style={styles.recentCertificateInfo}>
                <Text style={[styles.recentCertificateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  {stats.recentCertificate.title}
                </Text>
                <Text style={[styles.recentCertificateDate, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Earned on {stats.recentCertificate.issueDate}
                </Text>
              </View>
              
              <MaterialCommunityIcons name="chevron-right" size={24} color={darkMode ? '#a0aec0' : '#6b7280'} />
            </TouchableOpacity>
          </View>
        )}
        
        <View style={styles.certificatesList}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            {filter === 'all' ? 'All Certificates' : 
             filter === 'pinned' ? 'Pinned Certificates' :
             filter === 'verified' ? 'Verified Certificates' : 'Recent Certificates'}
            <Text style={styles.certificateCount}> ({filteredCertificates.length})</Text>
          </Text>
          
          {filteredCertificates.length === 0 ? (
            renderEmptyState()
          ) : (
            filteredCertificates.map((certificate) => renderCertificateCard(certificate))
          )}
        </View>
        
        {/* Spacer for bottom padding */}
        <View style={{ height: 100 }} />
      </Animated.ScrollView>
      
      {/* Modals */}
      {renderCertificateModal()}
      {renderShareOptionsModal()}
      
      {/* Floating Action Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: '#667eea' }]}
        onPress={() => navigation.navigate('AddCertificate')}
      >
        <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#667eea',
  },
  container: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    overflow: 'hidden',
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
    paddingBottom: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  menuButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  iconButton: {
    padding: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 16,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 12,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  scrollView: {
    flex: 1,
    marginTop: 160,
  },
  contentContainer: {
    paddingBottom: 100,
  },
  filtersContainer: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  recentCertificateSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  recentCertificate: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  recentCertificateIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  recentCertificateInfo: {
    flex: 1,
  },
  recentCertificateTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  recentCertificateDate: {
    fontSize: 14,
  },
  certificatesList: {
    paddingHorizontal: 20,
  },
  certificateCount: {
    fontSize: 14,
    fontWeight: 'normal',
    opacity: 0.7,
  },
  certificateCard: {
    marginBottom: 16,
  },
  certificateCardContent: {
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  certificateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  certificateIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  certificateTitleContainer: {
    flex: 1,
  },
  certificateTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  certificateId: {
    fontSize: 12,
  },
  pinButton: {
    padding: 4,
  },
  certificateSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  certificateDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 12,
    marginLeft: 4,
  },
  certificateFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#d1fae5',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  verifiedText: {
    fontSize: 12,
    color: '#065f46',
    marginLeft: 4,
    fontWeight: '500',
  },
  certificateTags: {
    flexDirection: 'row',
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginLeft: 4,
  },
  tagText: {
    fontSize: 10,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
    paddingHorizontal: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyStateButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 40 : 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  modalCertificateHeader: {
    alignItems: 'center',
    padding: 24,
    borderRadius: 20,
    marginBottom: 24,
  },
  modalCertificateIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalCertificateTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 8,
  },
  modalCertificateSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 16,
  },
  modalCertificateId: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  modalCertificateIdText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '500',
  },
  modalCertificateInfo: {
    marginBottom: 24,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 8,
  },
  infoContent: {
    marginLeft: 12,
  },
  infoLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  modalTags: {
    marginBottom: 24,
  },
  modalTagsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  modalTagsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  modalTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  modalTagText: {
    fontSize: 12,
  },
  verificationSection: {
    marginBottom: 24,
  },
  verificationTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  verificationCard: {
    padding: 20,
    borderRadius: 16,
    alignItems: 'center',
  },
  verificationCode: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    fontFamily: 'monospace',
  },
  verificationText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },
  verifyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  verifyButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  shareStats: {
    marginBottom: 24,
  },
  shareStatsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  shareStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  shareStat: {
    alignItems: 'center',
  },
  shareStatNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    marginVertical: 8,
  },
  shareStatLabel: {
    fontSize: 12,
  },
  modalActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  modalActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    flex: 1,
  },
  downloadButton: {
    backgroundColor: '#10b981',
  },
  downloadButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  shareButton: {
    backgroundColor: '#667eea',
  },
  shareButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  shareModal: {
    width: width * 0.9,
    borderRadius: 20,
    padding: 20,
  },
  shareModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 24,
  },
  shareOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  shareOption: {
    alignItems: 'center',
    width: '48%',
    marginBottom: 16,
  },
  shareOptionIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  shareOptionText: {
    fontSize: 14,
    textAlign: 'center',
  },
  cancelShareButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelShareText: {
    fontSize: 16,
    color: '#ef4444',
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});

export default Certificates;
